import classNames from 'classnames';
import React, { memo, useCallback, useMemo } from 'react';
import { v4 } from 'uuid';

import { SvgIconUrl } from 'common/components';

import svgIcons, { SvgProps } from 'common/svgIcons';

import './index.css';

type CheckboxProps = {
  className?: string;
  icon?: SvgProps;
  checked?: boolean;
  disabled?: boolean;
  dataAttr?: string;
  onChange?: (checked: boolean) => void;
  iconStyle?: 'rounded' | 'semi-rounded';
};

export const Checkbox: React.FC<React.PropsWithChildren<CheckboxProps>> = memo(
  ({
    checked,
    disabled = false,
    className: outsideClassName,
    onChange,
    iconStyle = 'rounded',
    dataAttr = '',
    children,
  }) => {
    const onClick = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => onChange && onChange(e.target.checked),
      [onChange],
    );

    const wrapperClassName = useMemo(
      () =>
        classNames({
          checkbox: true,
          'checkbox--checked': disabled ? false : checked,
          'checkbox--disabled': disabled,
          [`${outsideClassName}`]: outsideClassName,
        }),
      [disabled, checked, outsideClassName],
    );

    const iconSymbol = useMemo(() => (disabled ? svgIcons.LockIcon() : svgIcons.arrowCheck()), [disabled]);

    const iconClassName = useMemo(
      () =>
        classNames({
          checkbox__icon: true,
          'checkbox__icon--checked': disabled ? false : checked,
          [`checkbox__icon--${iconStyle}`]: iconStyle,
        }),
      [checked, disabled, iconStyle],
    );

    const id = useMemo(() => v4(), []);

    return (
      <div className={wrapperClassName}>
        {children && (
          <label className='checkbox__label' htmlFor={id}>
            {children}
          </label>
        )}
        <input
          type='checkbox'
          id={id}
          name={id}
          data-test={dataAttr}
          className='checkbox__input'
          disabled={disabled}
          onChange={onClick}
          checked={checked}
        />
        <SvgIconUrl className={iconClassName} symbol={iconSymbol} />
      </div>
    );
  },
);
